import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Loader2, UserPlus, Search, X, ImagePlus, Camera, User, Check,
  Package, AlertTriangle, MapPin, Home, Building2, DoorOpen,
} from 'lucide-react';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsPanel } from '@/components/ui/tabs';

import {
  buscarEmpleados,
  getEspaciosActivos,
  asignarEquipo,
  type AsignarEquipoPayload,
  type TipoUbicacion,
} from '@/services/api/inventario.api';
import { getApiErrorMessage } from '@/services/api/client';
import type { EmpleadoBusqueda, EspacioFisico, OrigenDatos } from '@/types/dominio';
import { cn } from '@/lib/utils';

/**
 * Forma mínima de un activo asignable. Tanto `MantenimientoItem` como
 * `InventarioItem` la satisfacen, así el dialog se reutiliza desde ambas vistas.
 */
export interface AsignableItem {
  id_item_maestro: string;
  serial_global: string;
  nombre_glpi: string | null;
  tipo: string | null;
  marca: string | null;
  origen_datos: OrigenDatos;
}

/** Pestaña activa del destino — solo aplica a activos de Compras. */
type DestinoTab = 'HOME_OFFICE' | 'OFICINA';

interface Props {
  /** Equipo a asignar — null cuando el dialog está cerrado. */
  item: AsignableItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Límites duros del backend, replicados aquí para validar en cliente y dar
// feedback inmediato sin esperar al 400.
const MAX_IMAGENES = 10;
const MAX_BYTES_POR_IMAGEN = 8 * 1024 * 1024;
const MIMES_PERMITIDOS = ['image/jpeg', 'image/png', 'image/webp'];

// Dirección de la sede — solo para mostrar la dirección resuelta en el form.
// La fuente de verdad es el backend (constante DIRECCION_OFICINA del service).
const DIRECCION_OFICINA = 'Calle 59 #13-33';

interface PreviewImg {
  file: File;
  previewUrl: string;   // object URL — se revoca al desmontar
}

export function AsignarEquipoDialog({ item, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Solo los activos de Compras pueden ir a un espacio físico — los GLPI
  // siempre van a una persona, igual que hoy. Determina si mostramos las
  // pestañas HOME OFFICE / OFICINA o el flujo clásico de un solo destino.
  const esCompras = item?.origen_datos === 'COMPRAS';

  // ── State del form ─────────────────────────────────────────────────────
  const [busquedaEmp, setBusquedaEmp] = useState('');
  const [empleadoSel, setEmpleadoSel] = useState<EmpleadoBusqueda | null>(null);
  const [destinoTab, setDestinoTab] = useState<DestinoTab>('HOME_OFFICE');
  const [busquedaEspacio, setBusquedaEspacio] = useState('');
  const [espacioSel, setEspacioSel] = useState<EspacioFisico | null>(null);
  const [observaciones, setObservaciones] = useState('');
  const [tipoUbicacion, setTipoUbicacion] = useState<TipoUbicacion>('OFICINA');
  const [imagenes, setImagenes] = useState<PreviewImg[]>([]);
  const [dragOver, setDragOver] = useState(false);

  // Reset al abrir/cerrar para que cada apertura sea limpia
  useEffect(() => {
    if (!open) {
      setBusquedaEmp('');
      setEmpleadoSel(null);
      setDestinoTab('HOME_OFFICE');
      setBusquedaEspacio('');
      setEspacioSel(null);
      setObservaciones('');
      setTipoUbicacion('OFICINA');
      // Liberar object URLs para no leakear memoria
      imagenes.forEach((i) => URL.revokeObjectURL(i.previewUrl));
      setImagenes([]);
      setDragOver(false);
    }
    // intencional: solo en cambio de `open`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Al cambiar de empleado, la modalidad vuelve a OFICINA para evitar arrastrar
  // una selección de TELETRABAJO inválida hacia alguien sin dirección registrada.
  // (Solo aplica a GLPI: en Compras/HOME OFFICE la modalidad siempre es TELETRABAJO.)
  useEffect(() => {
    setTipoUbicacion('OFICINA');
  }, [empleadoSel]);

  // Cambiar de pestaña resetea la selección de la otra — no queremos enviar
  // un id_empleado y un id_espacio arrastrados de una pestaña que ya no se ve.
  useEffect(() => {
    if (destinoTab === 'HOME_OFFICE') {
      setEspacioSel(null);
      setBusquedaEspacio('');
    } else {
      setEmpleadoSel(null);
      setBusquedaEmp('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinoTab]);

  // ── Búsqueda debounced de empleados (300ms) ────────────────────────────
  const [debouncedQ, setDebouncedQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(busquedaEmp.trim()), 300);
    return () => clearTimeout(t);
  }, [busquedaEmp]);

  const empleadosQuery = useQuery({
    queryKey: ['empleados-buscar', debouncedQ],
    queryFn: () => buscarEmpleados(debouncedQ),
    // Dispara siempre que el dialog esté abierto, no haya empleado elegido, y
    // la pestaña activa sea la de colaborador (irrelevante en Compras/OFICINA).
    enabled: open && !empleadoSel && (!esCompras || destinoTab === 'HOME_OFFICE'),
    staleTime: 60_000,
  });

  // ── Espacios físicos (solo Compras, pestaña OFICINA) ───────────────────
  const espaciosQuery = useQuery({
    queryKey: ['espacios-fisicos'],
    queryFn: getEspaciosActivos,
    enabled: open && esCompras && destinoTab === 'OFICINA',
    staleTime: 5 * 60_000,
  });

  const espaciosFiltrados = useMemo(() => {
    const lista = espaciosQuery.data ?? [];
    const q = busquedaEspacio.trim().toLowerCase();
    if (!q) return lista;
    return lista.filter((e) => e.nombre.toLowerCase().includes(q));
  }, [espaciosQuery.data, busquedaEspacio]);

  // ── Mutation ──────────────────────────────────────────────────────────
  const mutation = useMutation({
    mutationFn: (payload: AsignarEquipoPayload) =>
      asignarEquipo(item!.id_item_maestro, payload),
    onSuccess: (resp) => {
      const destino = resp.empleado ? resp.empleado.nombres : resp.espacio?.nombre;
      toast.success('Equipo asignado correctamente', {
        description:
          `${resp.serial_global} → ${destino}` +
          (resp.imagenes_subidas > 0 ? ` · ${resp.imagenes_subidas} foto(s)` : ''),
      });
      // Invalidar todo lo que muestre el estado del activo
      qc.invalidateQueries({ queryKey: ['mantenimientos'] });
      qc.invalidateQueries({ queryKey: ['inventario-dashboard'] });
      qc.invalidateQueries({ queryKey: ['inventario-items'] });
      qc.invalidateQueries({ queryKey: ['historial-activo', item?.id_item_maestro] });
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error('No se pudo asignar el equipo', {
        description: getApiErrorMessage(err),
      });
    },
  });

  // ── Helpers ────────────────────────────────────────────────────────────
  function agregarArchivos(files: FileList | File[]) {
    const arr = Array.from(files);
    const validos: PreviewImg[] = [];
    const errores: string[] = [];

    for (const file of arr) {
      if (!MIMES_PERMITIDOS.includes(file.type)) {
        errores.push(`${file.name}: tipo no permitido`);
        continue;
      }
      if (file.size > MAX_BYTES_POR_IMAGEN) {
        errores.push(`${file.name}: supera 8 MB`);
        continue;
      }
      validos.push({ file, previewUrl: URL.createObjectURL(file) });
    }

    const espacioRestante = MAX_IMAGENES - imagenes.length;
    if (validos.length > espacioRestante) {
      errores.push(`Solo puedes subir ${MAX_IMAGENES} fotos en total`);
      validos.splice(espacioRestante);
    }

    if (errores.length) {
      toast.warning('Algunas imágenes fueron descartadas', {
        description: errores.slice(0, 3).join(' · '),
      });
    }
    if (validos.length) {
      setImagenes((prev) => [...prev, ...validos]);
    }
  }

  function quitarImagen(idx: number) {
    setImagenes((prev) => {
      const out = [...prev];
      const [removed] = out.splice(idx, 1);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return out;
    });
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) {
      agregarArchivos(e.dataTransfer.files);
    }
  }

  const tamanoTotalMb = useMemo(
    () => imagenes.reduce((sum, i) => sum + i.file.size, 0) / (1024 * 1024),
    [imagenes],
  );

  // En Compras, la pestaña HOME OFFICE es siempre TELETRABAJO (no hay toggle);
  // en GLPI la modalidad la decide el toggle Oficina/Teletrabajo de siempre.
  const modalidadEmpleado: TipoUbicacion = esCompras ? 'TELETRABAJO' : tipoUbicacion;

  // Dirección de teletrabajo del empleado (si la tiene registrada).
  const direccionEmpleado = empleadoSel?.direccion?.trim() || '';
  // Bloqueo: no se puede entregar en teletrabajo sin una dirección de destino.
  const teletrabajoSinDireccion =
    modalidadEmpleado === 'TELETRABAJO' && !direccionEmpleado;

  // En Compras, el destino depende de la pestaña activa; en GLPI siempre es
  // el empleado (flujo clásico, sin pestañas).
  const puedeEnviarDestinoEmpleado = !!empleadoSel && !teletrabajoSinDireccion;
  const puedeEnviarDestinoEspacio = !!espacioSel;
  const puedeEnviar =
    !mutation.isPending &&
    (esCompras
      ? destinoTab === 'HOME_OFFICE' ? puedeEnviarDestinoEmpleado : puedeEnviarDestinoEspacio
      : puedeEnviarDestinoEmpleado);

  if (!item) return null;

  // ── Selector de colaborador — reutilizado en el flujo clásico (GLPI) y en
  //    la pestaña HOME OFFICE de Compras ────────────────────────────────
  const empleadoSelector = (
    <div className="grid gap-2">
      <Label>Colaborador que recibe</Label>

      {empleadoSel ? (
        <>
          <div className="flex items-center justify-between gap-3 rounded-md border border-emerald-200 bg-emerald-50/60 p-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-9 w-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                <Check className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{empleadoSel.nombres}</p>
                <p className="text-xs text-muted-foreground tabular">
                  Doc. {empleadoSel.documento}
                  {empleadoSel.cargo && ` · ${empleadoSel.cargo}`}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEmpleadoSel(null)}
              type="button"
            >
              Cambiar
            </Button>
          </div>

          {/* Alerta blanda: ya tiene equipos asignados ────────────── */}
          {empleadoSel.equipos_asignados.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50/70 p-3 text-sm">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-amber-900">
                    Esta persona ya tiene {empleadoSel.equipos_asignados.length}{' '}
                    equipo{empleadoSel.equipos_asignados.length === 1 ? '' : 's'} asignado{empleadoSel.equipos_asignados.length === 1 ? '' : 's'}
                  </p>
                  <p className="text-xs text-amber-800 mt-0.5">
                    Continúa solo si la entrega es adicional (ej: laptop + monitor).
                  </p>
                  <ul className="mt-2 space-y-1">
                    {empleadoSel.equipos_asignados.slice(0, 5).map((eq) => (
                      <li
                        key={eq.id_item_maestro}
                        className="text-xs text-amber-900 bg-white/60 rounded px-2 py-1 flex items-center gap-2"
                      >
                        <Package className="h-3 w-3 opacity-70 shrink-0" />
                        <span className="font-mono font-semibold">{eq.serial_global}</span>
                        {eq.nombre_glpi && (
                          <span className="text-amber-700 truncate">
                            · {eq.nombre_glpi}
                          </span>
                        )}
                      </li>
                    ))}
                    {empleadoSel.equipos_asignados.length > 5 && (
                      <li className="text-xs text-amber-700 pl-1">
                        y {empleadoSel.equipos_asignados.length - 5} más…
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              autoFocus
              placeholder="Buscar por nombre o documento..."
              value={busquedaEmp}
              onChange={(e) => setBusquedaEmp(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Resultados */}
          <div className="rounded-md border max-h-56 overflow-y-auto">
            {empleadosQuery.isLoading ? (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-6">
                <Loader2 className="h-4 w-4 animate-spin" />
                Buscando...
              </div>
            ) : !empleadosQuery.data?.length ? (
              <div className="flex flex-col items-center gap-1 text-sm text-muted-foreground py-6">
                <User className="h-5 w-5 opacity-40" />
                <p>No se encontraron colaboradores</p>
              </div>
            ) : (
              <ul className="divide-y">
                {empleadosQuery.data.map((emp) => {
                  const equiposCount = emp.equipos_asignados.length;
                  return (
                    <li key={emp.id_empleado}>
                      <button
                        type="button"
                        onClick={() => setEmpleadoSel(emp)}
                        className="w-full text-left px-3 py-2 hover:bg-muted/60 transition-colors flex items-center gap-3"
                      >
                        <div className="h-8 w-8 rounded-full bg-primary-50 text-primary-700 flex items-center justify-center text-xs font-semibold shrink-0">
                          {iniciales(emp.nombres)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{emp.nombres}</p>
                          <p className="text-xs text-muted-foreground tabular">
                            Doc. {emp.documento}
                            {emp.area && ` · ${emp.area}`}
                          </p>
                        </div>
                        {equiposCount > 0 && (
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold tabular shrink-0',
                              'bg-amber-100 text-amber-800 ring-1 ring-amber-200',
                            )}
                            title={`Ya tiene ${equiposCount} equipo(s) asignado(s)`}
                          >
                            <Package className="h-3 w-3" />
                            {equiposCount}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );

  // ── Ubicación del activo — toggle Oficina/Teletrabajo (solo GLPI; en
  //    Compras/HOME OFFICE la modalidad siempre es Teletrabajo) ──────────
  const ubicacionBlock = empleadoSel && (
    <div className="grid gap-2">
      <Label className="flex items-center gap-1.5">
        <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
        Ubicación del activo
      </Label>

      {!esCompras && (
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setTipoUbicacion('OFICINA')}
            aria-pressed={tipoUbicacion === 'OFICINA'}
            className={cn(
              'flex items-center gap-2 rounded-md border p-3 text-left transition-colors',
              tipoUbicacion === 'OFICINA'
                ? 'border-primary-500 bg-primary-50/70 ring-1 ring-primary-500'
                : 'border-border hover:bg-muted/50',
            )}
          >
            <Building2 className="h-4 w-4 shrink-0 text-primary-700" />
            <div className="min-w-0">
              <p className="text-sm font-medium">Oficina</p>
              <p className="text-xs text-muted-foreground truncate">Sede principal</p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setTipoUbicacion('TELETRABAJO')}
            aria-pressed={tipoUbicacion === 'TELETRABAJO'}
            className={cn(
              'flex items-center gap-2 rounded-md border p-3 text-left transition-colors',
              tipoUbicacion === 'TELETRABAJO'
                ? 'border-primary-500 bg-primary-50/70 ring-1 ring-primary-500'
                : 'border-border hover:bg-muted/50',
            )}
          >
            <Home className="h-4 w-4 shrink-0 text-primary-700" />
            <div className="min-w-0">
              <p className="text-sm font-medium">Hogar / Teletrabajo</p>
              <p className="text-xs text-muted-foreground truncate">
                Dirección del colaborador
              </p>
            </div>
          </button>
        </div>
      )}

      {/* Dirección resuelta — en modo lectura */}
      {modalidadEmpleado === 'OFICINA' ? (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <MapPin className="h-3.5 w-3.5 shrink-0" />
          El activo queda registrado en <span className="font-medium">{DIRECCION_OFICINA}</span>.
        </p>
      ) : teletrabajoSinDireccion ? (
        <p className="flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50/70 p-2 text-xs text-amber-900">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-700" />
          Este colaborador no tiene dirección registrada. Cargala en su ficha
          antes de asignar en modalidad teletrabajo.
        </p>
      ) : (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <MapPin className="h-3.5 w-3.5 shrink-0 text-primary-600" />
          El activo queda registrado en <span className="font-medium">{direccionEmpleado}</span>.
        </p>
      )}
    </div>
  );

  // ── Selector de espacio físico — pestaña OFICINA (solo Compras) ────────
  const espacioSelector = (
    <div className="grid gap-2">
      <Label className="flex items-center gap-1.5">
        <DoorOpen className="h-3.5 w-3.5 text-muted-foreground" />
        Espacio físico
      </Label>

      {espacioSel ? (
        <div className="flex items-center justify-between gap-3 rounded-md border border-emerald-200 bg-emerald-50/60 p-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
              <Check className="h-4 w-4" />
            </div>
            <p className="font-semibold text-sm truncate">{espacioSel.nombre}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setEspacioSel(null)} type="button">
            Cambiar
          </Button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar espacio (sala, oficina, recepción...)"
              value={busquedaEspacio}
              onChange={(e) => setBusquedaEspacio(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="rounded-md border max-h-56 overflow-y-auto">
            {espaciosQuery.isLoading ? (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-6">
                <Loader2 className="h-4 w-4 animate-spin" />
                Cargando...
              </div>
            ) : !espaciosFiltrados.length ? (
              <div className="flex flex-col items-center gap-1 text-sm text-muted-foreground py-6">
                <DoorOpen className="h-5 w-5 opacity-40" />
                <p>No se encontraron espacios</p>
              </div>
            ) : (
              <ul className="divide-y">
                {espaciosFiltrados.map((esp) => (
                  <li key={esp.id_espacio}>
                    <button
                      type="button"
                      onClick={() => setEspacioSel(esp)}
                      className="w-full text-left px-3 py-2 hover:bg-muted/60 transition-colors flex items-center gap-3"
                    >
                      <div className="h-8 w-8 rounded-full bg-primary-50 text-primary-700 flex items-center justify-center shrink-0">
                        <DoorOpen className="h-4 w-4" />
                      </div>
                      <p className="text-sm font-medium truncate">{esp.nombre}</p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-emerald-50 text-emerald-700">
              <UserPlus className="h-4 w-4" />
            </span>
            {esCompras ? 'Asignar activo' : 'Asignar equipo a colaborador'}
          </DialogTitle>
          <DialogDescription>
            {esCompras
              ? 'Registra la entrega a un colaborador (HOME OFFICE) o la ubicación en un espacio físico (OFICINA). Puedes adjuntar fotos del estado físico como evidencia.'
              : 'Registra la entrega del activo. Puedes adjuntar fotos del estado físico y los accesorios como evidencia.'}
          </DialogDescription>
        </DialogHeader>

        {/* ── Resumen del activo ──────────────────────────────────────── */}
        <div className="rounded-lg border bg-muted/30 p-3 text-sm grid grid-cols-2 gap-x-4 gap-y-1.5">
          <div className="flex justify-between gap-3 col-span-2">
            <span className="text-muted-foreground">Serial</span>
            <span className="font-mono font-semibold">{item.serial_global}</span>
          </div>
          {item.nombre_glpi && (
            <div className="flex justify-between gap-3 col-span-2">
              <span className="text-muted-foreground">Activo</span>
              <span className="font-medium text-right">{item.nombre_glpi}</span>
            </div>
          )}
          {item.tipo && (
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Tipo</span>
              <span className="capitalize">{item.tipo.toLowerCase()}</span>
            </div>
          )}
          {item.marca && (
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Marca</span>
              <span>{item.marca}</span>
            </div>
          )}
        </div>

        {/* ── Contenido scrolleable ───────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-4 pt-1">
          {/* ① Destino — pestañas HOME OFFICE / OFICINA solo para Compras */}
          {esCompras ? (
            <Tabs
              value={destinoTab}
              onValueChange={(v) => setDestinoTab(v as DestinoTab)}
              className="grid gap-3"
            >
              <TabsList className="grid grid-cols-2 gap-2">
                <TabsTrigger
                  value="HOME_OFFICE"
                  className={cn(
                    'flex items-center justify-center gap-2 rounded-md border p-2.5 text-sm font-medium transition-colors',
                    destinoTab === 'HOME_OFFICE'
                      ? 'border-primary-500 bg-primary-50/70 ring-1 ring-primary-500 text-primary-800'
                      : 'border-border text-muted-foreground hover:bg-muted/50',
                  )}
                >
                  <Home className="h-4 w-4" />
                  HOME OFFICE
                </TabsTrigger>
                <TabsTrigger
                  value="OFICINA"
                  className={cn(
                    'flex items-center justify-center gap-2 rounded-md border p-2.5 text-sm font-medium transition-colors',
                    destinoTab === 'OFICINA'
                      ? 'border-primary-500 bg-primary-50/70 ring-1 ring-primary-500 text-primary-800'
                      : 'border-border text-muted-foreground hover:bg-muted/50',
                  )}
                >
                  <Building2 className="h-4 w-4" />
                  OFICINA
                </TabsTrigger>
              </TabsList>

              <TabsPanel value="HOME_OFFICE" className="grid gap-4">
                {empleadoSelector}
                {ubicacionBlock}
              </TabsPanel>

              <TabsPanel value="OFICINA">
                {espacioSelector}
              </TabsPanel>
            </Tabs>
          ) : (
            <>
              {empleadoSelector}
              {ubicacionBlock}
            </>
          )}

          {/* ③ Observaciones */}
          <div className="grid gap-1.5">
            <Label htmlFor="observaciones">Observaciones (opcional)</Label>
            <Textarea
              id="observaciones"
              placeholder="Estado físico, accesorios entregados (cargador, mouse, maletín), número de acta de entrega…"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              rows={3}
              maxLength={1000}
            />
            <p className="text-xs text-muted-foreground text-right tabular">
              {observaciones.length}/1000
            </p>
          </div>

          {/* ④ Imágenes */}
          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <Label>Fotos de la entrega (opcional)</Label>
              <span className="text-xs text-muted-foreground tabular">
                {imagenes.length}/{MAX_IMAGENES}
                {imagenes.length > 0 && ` · ${tamanoTotalMb.toFixed(1)} MB`}
              </span>
            </div>

            {/* Dropzone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              aria-label="Subir fotos de la entrega"
              className={cn(
                'rounded-lg border-2 border-dashed transition-all cursor-pointer',
                'flex flex-col items-center justify-center gap-2 py-6 px-4 text-center',
                'hover:border-primary-400 hover:bg-primary-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                dragOver
                  ? 'border-primary-500 bg-primary-50/70'
                  : 'border-border bg-muted/20',
                imagenes.length >= MAX_IMAGENES && 'opacity-50 pointer-events-none',
              )}
            >
              <div className="h-10 w-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center">
                <ImagePlus className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-medium">
                  Arrastra las fotos aquí o{' '}
                  <span className="text-primary-700 underline underline-offset-2">selecciónalas</span>
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  JPG, PNG o WEBP · máx. 8 MB por imagen · hasta {MAX_IMAGENES} fotos
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files?.length) agregarArchivos(e.target.files);
                  // Reset para permitir volver a subir el mismo archivo
                  e.target.value = '';
                }}
              />
            </div>

            {/* Grid de previews */}
            {imagenes.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-1">
                {imagenes.map((img, idx) => (
                  <div
                    key={img.previewUrl}
                    className="relative group rounded-md overflow-hidden border bg-muted aspect-square"
                  >
                    <img
                      src={img.previewUrl}
                      alt={img.file.name}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => quitarImagen(idx)}
                      className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/90"
                      aria-label={`Quitar ${img.file.name}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-1.5 text-[10px] text-white truncate">
                      {img.file.name}
                    </div>
                  </div>
                ))}
                {imagenes.length < MAX_IMAGENES && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="aspect-square rounded-md border-2 border-dashed border-border hover:border-primary-400 hover:bg-primary-50/40 flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-primary-700 transition-colors"
                    aria-label="Agregar más fotos"
                  >
                    <Camera className="h-5 w-5" />
                    <span className="text-[10px] font-medium">Agregar</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="border-t pt-3 -mx-6 px-6">
          <div className="flex-1 text-xs text-muted-foreground">
            {!puedeEnviar && !mutation.isPending && (
              <Badge variant="outline" className="font-normal">
                {esCompras && destinoTab === 'OFICINA'
                  ? 'Selecciona un espacio para continuar'
                  : 'Selecciona un colaborador para continuar'}
              </Badge>
            )}
          </div>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            disabled={!puedeEnviar}
            onClick={() => {
              const payload: AsignarEquipoPayload = {
                observaciones: observaciones.trim() || undefined,
                imagenes: imagenes.map((i) => i.file),
              };
              if (esCompras && destinoTab === 'OFICINA') {
                payload.id_espacio = espacioSel!.id_espacio;
              } else {
                payload.id_empleado = empleadoSel!.id_empleado;
                payload.tipo_ubicacion = modalidadEmpleado;
              }
              mutation.mutate(payload);
            }}
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="animate-spin" />
                Asignando...
              </>
            ) : (
              <>
                <Check className="h-4 w-4" />
                Confirmar entrega
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Iniciales para el avatar — primera letra del nombre y del primer apellido. */
function iniciales(nombreCompleto: string): string {
  const partes = nombreCompleto.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  if (partes.length === 1) return partes[0].charAt(0).toUpperCase();
  return (partes[0].charAt(0) + partes[partes.length - 1].charAt(0)).toUpperCase();
}
